/**
 * Shared plumbing for the policy screens: refreshing what a policy change makes stale, and turning a failed generated
 * mutation (which rejects with the parsed server body) into the `ApiError` the form framework places on fields.
 */
import { useQueryClient } from '@tanstack/react-query';
import { getModelRuntimePolicyQueryKey } from '../../../generated/api/@tanstack/react-query.gen.js';
import { ApiError, normalizeApiError } from '../../../services/api/errors.js';
import { runtimePoliciesQuery, runtimePolicySurfaceRulesQuery } from '../../../services/api/runtime-policy.queries.js';

export const POLICIES_PATH = '/agents/libraries/model-runtime-policies';

/** The error to throw from a form's `onSubmit` for a rejected generated mutation. */
export function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : normalizeApiError(error, 0);
}

/**
 * Invalidate everything a policy create / update changes: the policy list, every per-kind-of-work option and rule
 * (they share the first key segment with their helper in `runtime-policy.queries.ts`), and the policy itself.
 */
export function useRefreshPolicies() {
  const queryClient = useQueryClient();
  return async (policyId?: number) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: runtimePoliciesQuery().queryKey.slice(0, 1) }),
      queryClient.invalidateQueries({ queryKey: runtimePolicySurfaceRulesQuery().queryKey.slice(0, 1) }),
      policyId === undefined
        ? undefined
        : queryClient.invalidateQueries({ queryKey: getModelRuntimePolicyQueryKey({ path: { id: policyId } }) }),
    ]);
  };
}
